/**
 * Warships — one hull per people, drawn side-on at 20 × 16 like the merchantman.
 *
 * **The same ship underneath.** Every people sails identical statistics (see
 * `UnitType.Warship`); what changes is what it looks like on the water, because
 * a fleet arriving over the horizon should tell you *whose* fleet it is before
 * you can read a health bar. Giving them different numbers as well would be a
 * balance surface nobody asked for and three times the tuning.
 *
 * Two frames each, and the animation is the same idea as the merchantman's: the
 * hull rides up and the wake shortens, so a ship under way visibly works rather
 * than sliding across the sea.
 *
 * `P`/`p` are the owner's colour and its shadow — the sail on a human carrack,
 * the deck plating on a dwarven ironclad, the sail on an elven cutter — so a
 * fleet reads as *yours* at a glance and not merely as "a ship".
 */

/** Human: a high-castled carrack, square rig, shields ranged along the rail. */
export const ART_UNITS_WARSHIP_HUMAN: string[][] = [
  [
    '         b          ',
    '        byy         ',
    '    PPPPPbPPPPP     ',
    '    PPPPPbPPPPP     ',
    '    yyyyybyyyyy     ',
    '    PPPPPbPPPPP     ',
    '     ppppbppp       ',
    '         b          ',
    '   bbbbbbbbbbbb     ',
    ' bmPmPmPmPmPmPmPmb  ',
    ' bbbbbbbbbbbbbbbbbb ',
    ' bwwwwwwwwwwwwwwwwb ',
    '  bwwwwwwwwwwwwwwb  ',
    '   ##############   ',
    '    cccccccccccc    ',
    '                    ',
  ],
  [
    '         b          ',
    '       yyb          ',
    '    ppppPbPPPPP     ',
    '    PPPPPbPPPPP     ',
    '    yyyyybyyyyy     ',
    '    PPPPPbPPPPP     ',
    '     ppppbppp       ',
    '         b          ',
    '                    ',
    '   bbbbbbbbbbbb     ',
    ' bPmPmPmPmPmPmPmPb  ',
    ' bbbbbbbbbbbbbbbbbb ',
    ' bwwwwwwwwwwwwwwwwb ',
    '  bwwwwwwwwwwwwwwb  ',
    '   ##############   ',
    '     cccccccc       ',
  ],
];

/**
 * Dwarf: a low ironclad. No sail at all — a riveted hull, a squat stack and a
 * ram at the waterline, because dwarves do not ask the wind for permission.
 */
export const ART_UNITS_WARSHIP_DWARF: string[][] = [
  [
    '                    ',
    '        d           ',
    '       ddd          ',
    '       dyd          ',
    '       ddd          ',
    '    mmmmmmmmmm      ',
    '   dPPPPPPPPPPd     ',
    '   dmdmdmdmdmdm     ',
    '  ddddddddddddddd   ',
    ' dmmmmmmmmmmmmmmmd  ',
    ' dPPPPPPPPPPPPPPPd  ',
    ' ddddddddddddddddd  ',
    '  dmmmmmmmmmmmmmd   ',
    '   ##############   ',
    '    cccccccccccc    ',
    '                    ',
  ],
  [
    '                    ',
    '        d           ',
    '       ddd          ',
    '       dyd          ',
    '       ddd          ',
    '                    ',
    '    mmmmmmmmmm      ',
    '   dppppppppppd     ',
    '   dmdmdmdmdmdm     ',
    '  ddddddddddddddd   ',
    ' dmmmmmmmmmmmmmmmd  ',
    ' dPPPPPPPPPPPPPPPd  ',
    ' ddddddddddddddddd  ',
    '  dmmmmmmmmmmmmmd   ',
    '   ##############   ',
    '     cccccccc       ',
  ],
];

/**
 * Elf: a swan-prowed cutter. Slender, a single tall triangular sail and living
 * green along the strakes — the hull is grown rather than nailed together.
 */
export const ART_UNITS_WARSHIP_ELF: string[][] = [
  [
    '        b           ',
    '       bPb          ',
    '      bPPPb         ',
    '     bPPPPPb        ',
    '    bPPPPPPPb       ',
    '   bpppppppppb      ',
    '        b           ',
    '  g     b        g  ',
    '  Gg    b       gG  ',
    '   ggggggggggggg    ',
    '  bGGGGGGGGGGGGGb   ',
    ' bwwwwwwwwwwwwwwwb  ',
    '  bwwwwwwwwwwwwwb   ',
    '   #############    ',
    '    cccccccccccc    ',
    '                    ',
  ],
  [
    '        b           ',
    '       bPb          ',
    '      bPPPb         ',
    '     bPPPPPb        ',
    '    bpppppppb       ',
    '   bPPPPPPPPPb      ',
    '        b           ',
    '  g     b        g  ',
    '   g    b       g   ',
    '  Gggggggggggggg    ',
    '  bGGGGGGGGGGGGGb   ',
    ' bwwwwwwwwwwwwwwwb  ',
    '  bwwwwwwwwwwwwwb   ',
    '   #############    ',
    '     cccccccc       ',
    '                    ',
  ],
];

/**
 * The hull a given people sails, by `Race`. Falls back to the human carrack for
 * anything without a fleet of its own — a monster camp never builds one, but a
 * sprite table with a hole in it draws nothing at all, and a missing ship is a
 * far worse bug than the wrong one.
 */
export const ART_UNITS_WARSHIP: string[][][] = [
  ART_UNITS_WARSHIP_HUMAN,
  ART_UNITS_WARSHIP_DWARF,
  ART_UNITS_WARSHIP_ELF,
];
